import { ReactNode, useEffect, useRef, useState } from "react";
import { ChevronDown, Lock, CheckCircle2 } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useExtractionMetadata } from "@/hooks/useExtractionMetadata";
import { PreFilledBadge } from "@/components/ui/PreFilledBadge";

interface FormFieldProps {
  label: string;
  name: string;
  error?: string;
  helperText?: string;
  required: boolean;
  whyText?: string;
  sensitive?: boolean;
  showValidCheck?: boolean;
  children: ReactNode;
}

export function FormField({
  label,
  name,
  error,
  helperText,
  required,
  whyText,
  sensitive = false,
  showValidCheck = false,
  children,
}: FormFieldProps) {
  const [isWhyExpanded, setIsWhyExpanded] = useState(false);
  const { fieldMetadata, markEdited } = useExtractionMetadata(name);
  // useFormContext may be undefined if this field is rendered outside a
  // FormProvider (e.g., future reuse). Guard safely.
  const formCtx = useFormContext();
  const isDirty = Boolean(formCtx?.formState?.dirtyFields && getByDotPath(formCtx.formState.dirtyFields, name));
  const hasMarkedEdited = useRef(false);
  useEffect(() => {
    if (
      fieldMetadata &&
      fieldMetadata.source === "extracted" &&
      isDirty &&
      !hasMarkedEdited.current
    ) {
      hasMarkedEdited.current = true;
      markEdited(name);
    }
  }, [fieldMetadata, isDirty, markEdited, name]);

  return (
    <div className="mb-6" data-error={error ? "true" : undefined}>
      {/* Label with required indicator */}
      <label htmlFor={name} className="form-label">
        {label}
        {required && <span className="ml-1" style={{ color: "var(--color-error)" }}>*</span>}
        {fieldMetadata && fieldMetadata.source !== "user_entered" && (
          <PreFilledBadge metadata={fieldMetadata} />
        )}
      </label>

      {/* Input container with optional sensitive treatment */}
      <div className={`mt-2 relative ${sensitive ? "form-field-sensitive" : ""}`}>
        {children}

        {/* Valid checkmark icon */}
        {showValidCheck && !error && (
          <CheckCircle2
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: "var(--color-success)" }}
          />
        )}

        {/* Sensitive field lock icon */}
        {sensitive && !showValidCheck && (
          <span
            className="absolute right-3 top-1/2 -translate-y-1/2 inline-flex"
            title={`Your ${label.toLowerCase()} is encrypted and never shared.`}
            aria-label={`Your ${label.toLowerCase()} is encrypted and never shared.`}
          >
            <Lock size={14} style={{ color: "var(--text-muted)" }} />
          </span>
        )}
      </div>

      {/* Helper text */}
      {helperText && !error && (
        <p className="form-helper">{helperText}</p>
      )}

      {/* Error message */}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}

      {/* Helper for reading dirtyFields by dotted path. react-hook-form
          represents dirtyFields as a nested object; we walk it. */}
      {/* Expandable "Why?" section */}
      {whyText && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setIsWhyExpanded(!isWhyExpanded)}
            className="flex items-center gap-1 text-xs transition-colors"
            style={{ color: "var(--text-muted)" }}
            aria-expanded={isWhyExpanded}
          >
            <ChevronDown
              size={14}
              className={`transition-transform duration-200 ${
                isWhyExpanded ? "rotate-180" : ""
              }`}
            />
            <span>Why do we need this?</span>
          </button>

          {isWhyExpanded && (
            <p
              className="mt-2 text-xs rounded-md p-3 animate-fade-in"
              style={{ color: "var(--text-secondary)", background: "var(--bg-page)" }}
            >
              {whyText}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function getByDotPath(obj: unknown, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}
